import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertCircle, ClipboardList, LayoutDashboard,
  MapPin, PlusCircle, Printer, Search, Star,
} from 'lucide-react';
import { useFarmsHub } from '../hooks/useFarmsHub';
import { useAuth } from '@/hooks/useAuth';
import { useCountUp } from '@/hooks/useCountUp';
import { PageShell } from '@/components/layout/PageShell';
import { staggerContainer, staggerItem } from '@/lib/motion';
import { formatBRL } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Farm, FarmOrder, FarmStatus, OrderStatus } from '../types';

// ─── Labels / chips ───────────────────────────────────────────────────────────

const FARM_LABELS: Record<FarmStatus, string> = {
  pending_review: 'Em revisão',
  active:         'Ativa',
  paused:         'Pausada',
  rejected:       'Rejeitada',
};
const FARM_CHIPS: Record<FarmStatus, string> = {
  pending_review: 'chip-warning',
  active:         'chip-positive',
  paused:         'chip-neutral',
  rejected:       'chip-negative',
};

const ORDER_LABELS: Record<OrderStatus, string> = {
  quote:     'Orçamento',
  reserved:  'Reservado',
  printing:  'Imprimindo',
  done:      'Concluído',
  cancelled: 'Cancelado',
  refunded:  'Reembolsado',
};
const ORDER_CHIPS: Record<OrderStatus, string> = {
  quote:     'chip-neutral',
  reserved:  'chip-info',
  printing:  'chip-warning',
  done:      'chip-positive',
  cancelled: 'chip-negative',
  refunded:  'chip-neutral',
};

// ─── Mini stat card ───────────────────────────────────────────────────────────

type Color = 'brand' | 'positive' | 'warning' | 'neutral';

const COLOR_CLS: Record<Color, string> = {
  brand:    'bg-[#14a655]/10 text-[#14a655]',
  positive: 'bg-positive/10 text-positive',
  warning:  'bg-warning/10  text-warning',
  neutral:  'bg-surface-subtle text-ink-muted',
};

function HubStat({
  label, value, sub, icon, color = 'brand',
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  color?: Color;
}) {
  const numValue = typeof value === 'number' ? value : null;
  const counted  = useCountUp(numValue ?? 0, { duration: 800 });

  return (
    <motion.div className="card p-4" variants={staggerItem}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <p className="text-xs font-medium text-ink-muted leading-snug">{label}</p>
        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-sm', COLOR_CLS[color])}>
          {icon}
        </div>
      </div>
      <p className="text-2xl font-bold text-ink tabular-nums leading-none">
        {numValue !== null ? counted : value}
      </p>
      {sub && <p className="text-xs text-ink-soft mt-1">{sub}</p>}
    </motion.div>
  );
}

// ─── Farm row ─────────────────────────────────────────────────────────────────

function FarmRow({ farm }: { farm: Farm }) {
  return (
    <Link
      to={`/farms/${farm.slug}`}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-surface-subtle transition-colors"
    >
      <div className="w-9 h-9 rounded-lg bg-[#14a655]/10 flex items-center justify-center shrink-0 text-lg overflow-hidden">
        {farm.logo_url
          ? <img src={farm.logo_url} alt={farm.name} className="w-full h-full object-cover" />
          : '🖨️'}
      </div>
      <p className="flex-1 text-sm font-medium text-ink truncate">{farm.name}</p>
      <span className={cn(FARM_CHIPS[farm.status], 'shrink-0 text-[11px]')}>
        {FARM_LABELS[farm.status]}
      </span>
    </Link>
  );
}

// ─── Order row ────────────────────────────────────────────────────────────────

function OrderRow({ order }: { order: FarmOrder }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-surface-subtle transition-colors">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-mono text-ink-soft">{order.id.slice(0, 8)}…</p>
        <p className="text-sm font-semibold text-ink tabular-nums">
          {formatBRL(order.total_amount_cents)}
        </p>
      </div>
      <span className={cn(ORDER_CHIPS[order.status], 'shrink-0 text-[11px]')}>
        {ORDER_LABELS[order.status]}
      </span>
    </div>
  );
}

// ─── FarmsHub page ────────────────────────────────────────────────────────────

export function FarmsHub() {
  const { user }               = useAuth();
  const { data, loading, error } = useFarmsHub();

  const activeFarms  = data.farms.filter((f) => f.status === 'active').length;
  const pendingFarms = data.farms.filter((f) => f.status === 'pending_review').length;
  const openOrders   = data.sellerOrders.filter((o) =>
    o.status === 'reserved' || o.status === 'printing'
  ).length;

  const firstName = (user?.email ?? '').split('@')[0] || 'usuário';

  if (loading) {
    return (
      <PageShell title="MYND Farms" subtitle="Carregando…">
        <div className="space-y-4">
          <div className="h-20 rounded-2xl bg-surface-subtle animate-pulse" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-28 rounded-2xl bg-surface-subtle animate-pulse" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="h-64 rounded-2xl bg-surface-subtle animate-pulse" />
            <div className="h-64 rounded-2xl bg-surface-subtle animate-pulse" />
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title="MYND Farms" subtitle="Sua central de impressão 3D distribuída.">

      {error && (
        <div className="mb-4 card p-4 flex items-start gap-3 text-negative">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Boas-vindas + CTAs */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4"
      >
        <div>
          <p className="font-semibold text-ink">Olá, {firstName}!</p>
          <p className="text-sm text-ink-muted mt-0.5">
            {data.farms.length === 0
              ? 'Cadastre sua farm para começar a receber pedidos de impressão 3D.'
              : `Você tem ${data.farms.length} farm(s) cadastrada(s) na rede.`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap shrink-0">
          <Link to="/farms/cadastrar" className="btn-primary gap-2 whitespace-nowrap">
            <PlusCircle className="w-4 h-4" />
            Cadastrar farm
          </Link>
          <Link to="/farms/marketplace" className="btn-secondary gap-2 whitespace-nowrap">
            <Search className="w-4 h-4" />
            Marketplace
          </Link>
        </div>
      </motion.div>

      {/* 4 stat cards */}
      <motion.div
        className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4"
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
        <HubStat
          label="Farms ativas"
          value={activeFarms}
          sub={pendingFarms > 0 ? `${pendingFarms} em revisão` : 'na rede'}
          icon={<Printer className="w-4 h-4" />}
          color="brand"
        />
        <HubStat
          label="Pedidos em aberto"
          value={openOrders}
          sub="como fornecedor"
          icon={<ClipboardList className="w-4 h-4" />}
          color={openOrders > 0 ? 'warning' : 'neutral'}
        />
        <HubStat
          label="Receita do mês"
          value={formatBRL(data.monthRevenueCents)}
          sub="pedidos concluídos"
          icon={<LayoutDashboard className="w-4 h-4" />}
          color="positive"
        />
        <HubStat
          label="Avaliação média"
          value={data.avgRating > 0 ? data.avgRating.toFixed(1) : '—'}
          sub={data.reviewsCount > 0 ? `${data.reviewsCount} avaliação(ões)` : 'sem avaliações'}
          icon={<Star className="w-4 h-4" />}
          color="brand"
        />
      </motion.div>

      {/* Duas colunas: minhas farms + meus pedidos */}
      <motion.div
        className="grid grid-cols-1 lg:grid-cols-2 gap-4"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
      >

        {/* Minhas farms */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="font-semibold text-ink">Minhas farms</p>
            <Link to="/farms/minhas" className="text-xs text-[#14a655] hover:underline">
              Ver todas →
            </Link>
          </div>

          {data.farms.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <div className="w-12 h-12 rounded-2xl bg-[#14a655]/10 flex items-center justify-center text-2xl">
                🖨️
              </div>
              <div>
                <p className="text-sm font-medium text-ink">Nenhuma farm cadastrada</p>
                <p className="text-xs text-ink-muted mt-0.5">
                  Cadastre sua farm para aparecer no marketplace.
                </p>
              </div>
              <Link to="/farms/cadastrar" className="btn-primary !py-1.5 !text-xs gap-1.5">
                <PlusCircle className="w-3.5 h-3.5" />
                Cadastrar agora
              </Link>
            </div>
          ) : (
            <div className="space-y-1">
              {data.farms.slice(0, 5).map((f) => <FarmRow key={f.id} farm={f} />)}
            </div>
          )}
        </div>

        {/* Meus pedidos como comprador */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="font-semibold text-ink">Meus pedidos</p>
            <Link to="/farms/pedidos" className="text-xs text-[#14a655] hover:underline">
              Ver todos →
            </Link>
          </div>

          {data.buyerOrders.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <div className="w-12 h-12 rounded-2xl bg-surface-subtle flex items-center justify-center">
                <MapPin className="w-6 h-6 text-ink-soft" />
              </div>
              <div>
                <p className="text-sm font-medium text-ink">Nenhum pedido realizado</p>
                <p className="text-xs text-ink-muted mt-0.5">
                  Encontre uma farm e faça seu primeiro pedido.
                </p>
              </div>
              <Link to="/farms/marketplace" className="btn-secondary !py-1.5 !text-xs gap-1.5">
                <Search className="w-3.5 h-3.5" />
                Explorar marketplace
              </Link>
            </div>
          ) : (
            <div className="space-y-1">
              {data.buyerOrders.slice(0, 5).map((o) => <OrderRow key={o.id} order={o} />)}
            </div>
          )}
        </div>

      </motion.div>
    </PageShell>
  );
}
